import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Progress, Modal, message } from 'antd';
import {
  CalendarCheck,
  Pickaxe,
  Coins,
  Flame,
  CheckCircle2,
  ThumbsUp,
  Share2,
  Target,
  Gift,
  Sparkles,
  HelpCircle,
} from 'lucide-react';
import {
  getMinerStatus,
  collectMiner,
  dailyCheckIn,
  getQuests,
  setQuestReminder,
  getBlindBox,
  openBlindBox,
  getCreationStatus,
  MinerStatus,
  QuestStatus,
  BlindBoxInfo,
  BlindBoxTier,
  OpenBoxResult,
  CreationStatus,
} from '../../api/payment/gamification.api';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import { routePaths } from '../../routes/routes-path';
import Nametag from '../../components/cosmetics/nametag.component';
import AvatarFrame from '../../components/cosmetics/avatar-frame';
import { FrameRarity } from '../../components/cosmetics/cosmetic-visuals';
import RewardBurst from '../../components/gamification/reward-burst';
import boxBasic from '../../images/blindbox/box-basic.png';
import boxRare from '../../images/blindbox/box-rare.png';
import boxEpic from '../../images/blindbox/box-epic.png';
import boxVip from '../../images/blindbox/box-vip.png';

// Thiết kế theo hạng (khớp Figma): ảnh hộp + tông nền ô + màu giá + màu nút.
const TIER_DESIGN: Record<string, { img: string; tint: string; price: string; btn?: string }> = {
  basic: { img: boxBasic, tint: 'bg-sky-50 dark:bg-sky-500/10', price: 'text-sky-600 dark:text-sky-400' },
  rare: { img: boxRare, tint: 'bg-violet-50 dark:bg-violet-500/10', price: 'text-violet-600 dark:text-violet-400' },
  epic: { img: boxEpic, tint: 'bg-amber-50 dark:bg-amber-500/10', price: 'text-amber-600 dark:text-amber-400' },
  vip: { img: boxVip, tint: 'bg-rose-50 dark:bg-rose-500/10', price: 'text-rose-600 dark:text-rose-400', btn: '#EF4444' },
};
const tierDesign = (k: string) => TIER_DESIGN[k] || TIER_DESIGN.basic;

const RARITY: Record<string, { label: string; color: string; bg: string }> = {
  Common: { label: 'Cổ Điển', color: '#9CA3AF', bg: 'rgba(156,163,175,.15)' },
  Rare: { label: 'Hiếm', color: '#3B82F6', bg: 'rgba(59,130,246,.15)' },
  Epic: { label: 'Sử Thi', color: '#8B5CF6', bg: 'rgba(139,92,246,.15)' },
  Legendary: { label: 'Huyền Thoại', color: '#F59E0B', bg: 'rgba(245,158,11,.12)' },
  Mythic: { label: 'Thần Thoại', color: '#EC4899', bg: 'rgba(236,72,153,.12)' },
};

// Hiệu ứng theo hạng hộp (giá cao → hoành tráng hơn, kiểu mở thẻ game).
const TIER_STYLE: Record<string, { grad: string; glow: string; ring: string; emoji: string }> = {
  basic: { grad: 'from-slate-400 to-slate-600', glow: '#94a3b8', ring: 'ring-slate-400', emoji: '📦' },
  rare: { grad: 'from-violet-500 to-fuchsia-600', glow: '#a855f7', ring: 'ring-violet-400', emoji: '🎁' },
  epic: { grad: 'from-sky-400 to-cyan-600', glow: '#0ea5e9', ring: 'ring-sky-400', emoji: '💎' },
  vip: { grad: 'from-amber-400 via-rose-500 to-fuchsia-600', glow: '#f59e0b', ring: 'ring-amber-400', emoji: '👑' },
};
const tierStyle = (t?: string) => TIER_STYLE[t || 'basic'] || TIER_STYLE.basic;

const fmtCountdown = (s: number) => {
  if (s <= 0) return '00:00:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return [h, m, sec].map((n) => String(n).padStart(2, '0')).join(':');
};

const DaoCaPage = () => {
  const { isOn } = useFeatureFlags();
  const navigate = useNavigate();
  const profile = useSelector((s: RootState) => s.features.auth.profile) as any;
  const myName = profile?.fullname || profile?.fullName || profile?.username || 'Bạn';
  const myAvatar = profile?.avatar || '/default-avatar.svg';
  // Pha reveal: 'burst' (đang mở, hộp rung) → 'shown' (bung quà + confetti).
  const [revealPhase, setRevealPhase] = useState<'burst' | 'shown'>('burst');
  const [status, setStatus] = useState<MinerStatus | null>(null);
  const [quests, setQuests] = useState<QuestStatus[]>([]);
  // Công tắc nhắc nhiệm vụ (BE trả questReminderOff trong miner/status).
  const [remindOn, setRemindOn] = useState(true);
  const [savingRemind, setSavingRemind] = useState(false);

  const toggleRemind = (next: boolean) => {
    if (savingRemind) return;
    const prev = remindOn;
    setRemindOn(next);           // đổi ngay cho mượt
    setSavingRemind(true);
    setQuestReminder(next)
      .then((r) => toast.success(r?.message || 'Đã cập nhật'))
      .catch(() => {
        setRemindOn(prev);       // lưu hỏng thì trả lại trạng thái cũ, không để hiện sai
        toast.error('Không cập nhật được cài đặt nhắc nhiệm vụ.');
      })
      .finally(() => setSavingRemind(false));
  };
  const [box, setBox] = useState<BlindBoxInfo | null>(null);
  const [creation, setCreation] = useState<CreationStatus | null>(null);
  const [oddsTier, setOddsTier] = useState<string | null>(null); // đang xem tỉ lệ hạng nào
  const [pickTier, setPickTier] = useState<BlindBoxTier | null>(null); // đang mở màn chọn hộp may mắn
  const [opening, setOpening] = useState(false);
  const [reveal, setReveal] = useState<OpenBoxResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  // mốc thời điểm fetch để tính dồn Ca chờ + đếm ngược ở client (mượt từng giây)
  const fetchedAt = useRef<number>(0);
  const [now, setNow] = useState<number>(0); // tick

  const load = async () => {
    try {
      const [s, q, b, c] = await Promise.all([
        getMinerStatus(),
        getQuests(),
        getBlindBox(),
        getCreationStatus().catch(() => null),
      ]);
      setStatus(s);
      setRemindOn(!s?.questReminderOff);   // BE là cờ TẮT, công tắc hiện là BẬT
      setQuests(q);
      setBox(b);
      setCreation(c);
      fetchedAt.current = Date.now();
    } catch {
      message.error('Không tải được trạng thái Ca đào.');
    } finally {
      setLoading(false);
    }
  };

  // Chọn 1 trong 4 hộp (may mắn) → gọi mở theo hạng → hiện kết quả.
  const pickAndOpen = async (tierKey: string) => {
    if (opening) return;
    setOpening(true);
    try {
      const r = await openBlindBox(tierKey);
      if (!r.success) {
        message.info(r.message);
        setPickTier(null);
        return;
      }
      setPickTier(null);
      setReveal(r);
      await load(); // cập nhật số dư + kho
    } catch {
      message.error('Mở hộp thất bại, thử lại.');
    } finally {
      setOpening(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Mở modal reveal: rung hộp ~0.65s rồi bung quà + confetti.
  useEffect(() => {
    if (!reveal) return;
    setRevealPhase('burst');
    const t = setTimeout(() => setRevealPhase('shown'), 650);
    return () => clearTimeout(t);
  }, [reveal]);

  // dẫn xuất live từ status + thời gian đã trôi kể từ lúc fetch
  const elapsed = status ? Math.max(0, (now - fetchedAt.current) / 1000) : 0;
  const livePending = status
    ? Math.min(status.capCa, Math.floor(status.pending + (status.ratePerHour * elapsed) / 3600))
    : 0;
  const liveCountdown = status ? Math.max(0, status.secondsUntilCap - elapsed) : 0;
  const liveCreationLeft = creation ? Math.max(0, creation.secondsLeft - elapsed) : 0;
  const capped = status ? livePending >= status.capCa : false;
  const pct = status ? Math.round((livePending / status.capCa) * 100) : 0;

  const doCheckIn = async () => {
    setActing(true);
    try {
      const r = await dailyCheckIn();
      r.success ? message.success(r.message) : message.info(r.message);
      await load();
    } catch {
      message.error('Điểm danh thất bại, thử lại.');
    } finally {
      setActing(false);
    }
  };

  const doCollect = async () => {
    setActing(true);
    try {
      const r = await collectMiner();
      r.success ? message.success(r.message) : message.info(r.message);
      await load();
    } catch {
      message.error('Thu Ca thất bại, thử lại.');
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-5">
      {/* Header + số dư */}
      <div className="ui-card rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500/15 text-amber-500 flex items-center justify-center shrink-0">
              <Pickaxe size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold ui-heading">Kho Ca đào</h1>
              <p className="ui-muted text-sm mt-0.5">
                Điểm danh &amp; đào Ca mỗi ngày — Ca đào dùng để mở Blind Box đổi vật phẩm.
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs ui-muted mb-0.5">Số dư Ca đào</div>
            <div className="inline-flex items-center gap-1.5 text-xl font-extrabold text-amber-500">
              {status?.minedCa ?? 0} Ca <Coins size={20} />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="ui-card rounded-2xl p-8 text-center ui-muted">Đang tải…</div>
      ) : (
        <>
          {/* Điểm danh */}
          <div className="ui-card rounded-2xl p-5 mb-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/15 text-emerald-500 flex items-center justify-center">
                  <CalendarCheck />
                </div>
                <div>
                  <div className="font-semibold ui-text">Điểm danh hàng ngày</div>
                  <div className="ui-muted text-sm flex items-center gap-2">
                    +{status?.dailyReward ?? 5} Ca · bật máy đào
                    {(status?.checkInStreak ?? 0) > 0 && (
                      <span className="inline-flex items-center gap-1 text-orange-500">
                        <Flame size={14} /> chuỗi {status?.checkInStreak} ngày
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Button
                type="primary"
                loading={acting}
                disabled={!status?.canCheckInToday}
                onClick={doCheckIn}
              >
                {status?.canCheckInToday ? 'Điểm danh' : 'Đã điểm danh'}
              </Button>
            </div>
          </div>

          {/* Máy đào */}
          <div className="ui-card rounded-2xl p-5 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-500 flex items-center justify-center">
                <Pickaxe />
              </div>
              <div className="flex-1">
                <div className="font-semibold ui-text">Máy đào Ca</div>
                <div className="ui-muted text-sm">
                  {status?.ratePerHour ?? 5} Ca/giờ · tối đa {status?.capCa ?? 15} Ca mỗi 3 giờ
                </div>
              </div>
              <span
                className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${
                  status?.minerActive
                    ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                    : 'bg-slate-500/10 ui-muted'
                }`}
              >
                {status?.minerActive ? 'Đang hoạt động' : 'Chưa hoạt động'}
              </span>
            </div>

            {!status?.minerActive ? (
              <div className="text-center ui-muted text-sm py-3">
                Máy đào chưa bật. Hãy <b>điểm danh</b> để khởi động máy ⛏️
              </div>
            ) : (
              <>
                <div className="flex items-end justify-between mb-1">
                  <span className="text-2xl font-bold text-amber-500">
                    {livePending} <span className="text-sm ui-muted font-normal">/ {status?.capCa} Ca chờ thu</span>
                  </span>
                  <span className="text-xs ui-muted tabular-nums">
                    {capped ? 'Đã đầy — thu ngay!' : `Đầy sau ${fmtCountdown(liveCountdown)}`}
                  </span>
                </div>
                <Progress
                  percent={pct}
                  showInfo={false}
                  strokeColor={capped ? '#f59e0b' : '#fbbf24'}
                  trailColor="rgba(148,163,184,0.25)"
                />
                <Button
                  className="mt-3"
                  block
                  type="primary"
                  loading={acting}
                  disabled={livePending <= 0}
                  onClick={doCollect}
                  style={{ background: '#f59e0b', borderColor: '#f59e0b' }}
                >
                  Thu {livePending > 0 ? `${livePending} ` : ''}Ca & đào tiếp
                </Button>
                {capped && (
                  <p className="text-center text-xs text-amber-600 mt-2">
                    Máy đã đầy — vào thu để đào tiếp (mỗi 3 giờ quay lại để không phí Ca).
                  </p>
                )}
              </>
            )}
          </div>

          {/* Nhiệm vụ hàng ngày */}
          <div className="ui-card rounded-2xl p-5 mb-4">
            <div className="font-semibold ui-text mb-3 flex items-center gap-2">
              <Target size={18} className="text-sky-500" /> Nhiệm vụ hàng ngày
            </div>
            <div className="divide-y ui-divider">
              {quests.map((q) => {
                const Icon =
                  q.key === 'CHECKIN' ? CalendarCheck : q.key === 'SHARE_POST' ? Share2 : ThumbsUp;
                return (
                  <div key={q.key} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                        q.done ? 'bg-emerald-500/15 text-emerald-500' : 'bg-slate-500/10 ui-muted'
                      }`}
                    >
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="ui-text text-sm font-medium truncate">{q.label}</div>
                      <div className="ui-muted text-xs mt-0.5 flex items-center gap-1">
                        {q.current}/{q.target} hoàn thành
                        {q.done && <CheckCircle2 className="text-emerald-500" size={13} />}
                      </div>
                    </div>
                    <span className="text-amber-500 font-semibold text-sm shrink-0">+{q.reward} Ca</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs ui-muted mt-3 rounded-lg bg-slate-500/5 px-3 py-2">
              * Thích / chia sẻ bài viết ngoài bảng tin để tự động nhận Ca đào khi đủ mục tiêu.
            </p>

            {/* Nhắc nhiệm vụ: chỉ gửi khi CHƯA làm xong trong ngày, tối đa 1 lần/ngày. */}
            <label className="mt-3 flex cursor-pointer select-none items-start gap-2">
              <input
                type="checkbox"
                checked={remindOn}
                disabled={savingRemind}
                onChange={(e) => toggleRemind(e.target.checked)}
                className="mt-[3px] h-4 w-4 shrink-0 rounded accent-sky-500"
              />
              <span className="text-sm ui-text">
                Nhắc tôi làm nhiệm vụ
                <span className="block text-xs ui-muted">
                  Tối đa 1 thông báo mỗi ngày, và chỉ gửi khi bạn chưa làm xong nhiệm vụ
                  hôm đó.
                </span>
              </span>
            </label>
          </div>

          {/* Chuỗi Sáng Tạo — nhiệm vụ sáng tạo theo tuần (3 bậc + đếm ngược) */}
          {creation && (
            <div className="ui-card rounded-2xl p-5 mb-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="font-semibold ui-text flex items-center gap-2">
                  <Sparkles size={18} className="text-fuchsia-500" /> Chuỗi Sáng Tạo
                </div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-500 tabular-nums">
                  <Flame size={14} /> Còn {fmtCountdown(liveCreationLeft)}
                </div>
              </div>
              <p className="ui-muted text-xs mb-3">
                Đăng bài / làm nội dung để hoàn thành bậc trong tuần — hết tuần làm lại. Đã đăng:{' '}
                <b>{creation.contentCount}</b> · Lượt thích nhận được: <b>{creation.engagementCount}</b>
              </p>
              {creation.streakMilestone > 0 && (
                <div className="flex items-center gap-2 text-xs mb-3 rounded-lg bg-orange-500/10 text-orange-600 dark:text-orange-400 px-3 py-2">
                  <Flame size={14} className="shrink-0" />
                  <span>
                    Chuỗi <b>{creation.currentStreak}</b> tuần liên tiếp · còn{' '}
                    <b>{creation.streakMilestone - (creation.currentStreak % creation.streakMilestone)}</b> tuần nữa để
                    thưởng <b>+{creation.streakBonus} Ca</b>
                  </span>
                </div>
              )}
              <div className="text-xs mb-3 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400">
                  🏅 Nhà Sáng Tạo: {creation.levelName}
                </span>
                {creation.levelIndex < 3 && (
                  <span className="ui-muted">
                    {creation.levelScore}/{creation.levelNextThreshold} điểm tới cấp kế
                  </span>
                )}
              </div>
              <div className="divide-y ui-divider">
                {creation.tiers.map((t) => (
                  <div key={t.tier} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 font-bold text-sm ${
                        t.rewarded
                          ? 'bg-emerald-500/15 text-emerald-500'
                          : 'bg-slate-500/10 ui-muted'
                      }`}
                    >
                      {t.tier}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="ui-text text-sm font-medium">
                        Bậc {t.tier} · {t.label}
                      </div>
                      <div className="ui-muted text-xs mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span>
                          Đăng {Math.min(creation.contentCount, t.contentTarget)}/{t.contentTarget}
                        </span>
                        <span>
                          · Thích {Math.min(creation.engagementCount, t.engageTarget)}/{t.engageTarget}
                        </span>
                        {t.rewarded && (
                          <span className="text-emerald-500 inline-flex items-center gap-1">
                            <CheckCircle2 size={13} /> Đã nhận
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-amber-500 font-semibold text-sm">+{t.reward} Ca</span>
                      {t.tier === 3 && (
                        <div className="text-[10px] font-medium text-fuchsia-500">+ Khung Nhà Sáng Tạo</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Button type="primary" className="mt-4 w-full" onClick={() => navigate(routePaths.home)}>
                Đăng nội dung ngay
              </Button>
            </div>
          )}

          {/* Blind Box — nhiều hạng (ẩn khi admin tắt) */}
          {isOn('feature.blindbox') && (
          <div className="ui-card rounded-2xl p-5 mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-fuchsia-500/15 text-fuchsia-500 flex items-center justify-center">
                <Gift />
              </div>
              <div className="flex-1">
                <div className="font-semibold ui-text">Blind Box</div>
                <div className="ui-muted text-sm">
                  Mở hộp bằng Ca đào — chọn 1 trong 4 hộp may mắn để thử vận may của bạn!
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {box?.tiers.map((t) => {
                const d = tierDesign(t.key);
                const enough = box.minedCa >= t.price;
                return (
                  <div key={t.key} className={`rounded-2xl p-3 text-center ${d.tint}`}>
                    <div className="mx-auto mb-2 flex aspect-square items-center justify-center rounded-xl bg-white dark:bg-white/90 p-2 shadow-sm">
                      <img src={d.img} alt={t.label} className="h-full w-full object-contain" loading="lazy" />
                    </div>
                    <div className="text-sm font-bold ui-text truncate">{t.label}</div>
                    <div className={`text-sm font-extrabold mt-0.5 ${d.price}`}>{t.price} Ca</div>
                    <Button
                      type="primary"
                      block
                      className="mt-2"
                      disabled={!enough}
                      onClick={() => setPickTier(t)}
                      style={enough && d.btn ? { background: d.btn, borderColor: d.btn } : undefined}
                    >
                      Mở hộp
                    </Button>
                    <button
                      className="ui-muted mt-2 text-xs hover:underline"
                      onClick={() => setOddsTier((v) => (v === t.key ? null : t.key))}
                    >
                      {oddsTier === t.key ? 'Ẩn tỉ lệ' : 'Tỉ lệ trúng'}
                    </button>
                    {oddsTier === t.key && (
                      <div className="mt-1.5 space-y-0.5 text-left">
                        {t.odds
                          .filter((o) => o.percent > 0)
                          .map((o) => (
                            <div key={o.label} className="flex justify-between text-[10px] ui-muted">
                              <span className="truncate">{o.label}</span>
                              <span className="tabular-nums shrink-0">{o.percent}%</span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] ui-muted mt-3 rounded-lg bg-slate-500/5 px-3 py-2">
              * Chỉ dùng Ca đào để mở hộp (không dùng tiền). Vật phẩm gồm Nametag, thưởng Ca, hiệu ứng Avatar…
              Trúng cosmetic dùng {box?.cosmeticDaysMin}-{box?.cosmeticDaysMax} ngày; bản độc bản Thần Thoại
              (đánh số 01–12) vĩnh viễn chỉ có ở Cửa hàng. Hộp VIP có tỉ lệ trúng vật phẩm hiếm cao nhất.
            </p>
          </div>
          )}

          {/* Cách hoạt động */}
          <div className="ui-card rounded-2xl p-5">
            <div className="font-semibold ui-text mb-3 flex items-center gap-2">
              <HelpCircle size={18} className="text-sky-500" /> Cách hoạt động
            </div>
            <div className="space-y-3 text-sm ui-muted">
              {[
                ['📆', 'Điểm danh mỗi ngày nhận Ca & kích hoạt máy đào.'],
                ['⚡', `Máy đào tự tích ${status?.ratePerHour ?? 5} Ca/giờ, tối đa ${status?.capCa ?? 15} Ca sau 3 giờ.`],
                ['↩️', 'Quay lại thu Ca & đào tiếp — càng chăm càng nhiều Ca.'],
                ['🔒', 'Ca đào chỉ mở Blind Box và vật phẩm, không quy đổi tiền.'],
              ].map(([icon, text]) => (
                <div key={text} className="flex items-start gap-3">
                  <span className="text-base leading-5">{icon}</span>
                  <span className="ui-text">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Modal CHỌN HỘP may mắn (1 trong 4) */}
      <Modal open={!!pickTier} onCancel={() => !opening && setPickTier(null)} footer={null} centered width={360}>
        {pickTier &&
          (() => {
            const st = tierStyle(pickTier.key);
            return (
              <div className="text-center py-3">
                <div className="font-bold ui-text text-lg">{pickTier.label}</div>
                <div className="ui-muted text-sm mb-4">
                  Chọn 1 trong 4 hộp — chúc bạn may mắn! ({pickTier.price} Ca)
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[0, 1, 2, 3].map((i) => (
                    <button
                      key={i}
                      disabled={opening}
                      onClick={() => pickAndOpen(pickTier.key)}
                      className={`aspect-square rounded-2xl bg-gradient-to-br ${st.grad} flex items-center justify-center p-3 transition hover:scale-105 active:scale-95 disabled:opacity-60 ring-2 ${st.ring}`}
                      style={{ boxShadow: `0 0 22px ${st.glow}55` }}
                    >
                      {opening ? (
                        <span className="text-4xl">…</span>
                      ) : (
                        <img src={tierDesign(pickTier.key).img} alt="" className="h-full w-full object-contain drop-shadow" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
      </Modal>

      {/* Modal reveal phần thưởng — rung hộp → nổ quà (confetti) → hiện vật phẩm thật */}
      <Modal open={!!reveal} onCancel={() => setReveal(null)} footer={null} centered width={360}>
        {reveal &&
          (() => {
            const st = tierStyle(reveal.tier);
            const rc = RARITY[reveal.rarity || ''];
            const miss = reveal.kind === 'Miss';
            const isItem = reveal.kind === 'Item';
            const shown = revealPhase === 'shown';
            const rar = (reveal.rarity || '').toLowerCase() as FrameRarity;
            return (
              <div className="relative overflow-hidden text-center py-4">
                <style>{`
                  @keyframes rb-pop{0%{transform:scale(.3);opacity:0}60%{transform:scale(1.12);opacity:1}100%{transform:scale(1)}}
                  @keyframes rb-shake{0%,100%{transform:translateX(0) rotate(0)}20%{transform:translateX(-6px) rotate(-6deg)}40%{transform:translateX(6px) rotate(6deg)}60%{transform:translateX(-4px) rotate(-4deg)}80%{transform:translateX(4px) rotate(4deg)}}
                  .rb-pop{animation:rb-pop .5s cubic-bezier(.2,1.3,.4,1) both}
                  .rb-shake{animation:rb-shake .5s ease-in-out infinite}
                `}</style>

                {shown && !miss && (
                  <RewardBurst colors={[st.glow, '#ffffff', rc?.color || '#f59e0b', '#f43f5e', '#a855f7']} />
                )}

                {/* Pha rung hộp (trước khi bung) */}
                {!shown && (
                  <div className="py-6">
                    <div
                      className={`rb-shake mx-auto w-28 h-28 rounded-2xl flex items-center justify-center bg-gradient-to-br ${st.grad}`}
                      style={{ boxShadow: `0 0 44px ${st.glow}88` }}
                    >
                      <Gift size={54} className="text-white" />
                    </div>
                    <div className="ui-muted text-sm mt-4">Đang mở hộp…</div>
                  </div>
                )}

                {/* Pha bung quà */}
                {shown && (
                  <div className="relative z-10 rb-pop">
                    {miss && (
                      <>
                        <div className="mx-auto mb-3 w-24 h-24 rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                          <span className="text-5xl">🍀</span>
                        </div>
                        <div className="text-lg font-bold ui-text">Chúc may mắn lần sau!</div>
                        <div className="ui-muted text-sm">Thử lại hộp khác nhé 🎁</div>
                      </>
                    )}

                    {reveal.kind === 'Ca' && (
                      <>
                        <div
                          className="mx-auto mb-3 w-24 h-24 rounded-2xl flex items-center justify-center bg-gradient-to-br from-amber-300 to-amber-500"
                          style={{ boxShadow: '0 0 40px #f59e0b88' }}
                        >
                          <Coins size={50} className="text-white" />
                        </div>
                        <div className="text-2xl font-extrabold text-amber-500">+{reveal.caReward} Ca đào</div>
                        <div className="ui-muted text-sm">Đã cộng vào ví Ca đào 🪙</div>
                      </>
                    )}

                    {isItem && (
                      <>
                        <div className="text-xs font-semibold mb-2" style={{ color: rc?.color || '#f59e0b' }}>
                          {rc?.label || reveal.rarity}
                          {reveal.mintNumber ? (
                            <span className="ml-2 rounded-full bg-gradient-to-r from-amber-400 to-fuchsia-500 px-2 py-0.5 text-white">
                              #{String(reveal.mintNumber).padStart(2, '0')} độc bản
                            </span>
                          ) : null}
                        </div>

                        {/* PREVIEW vật phẩm thật (khớp y hệt lúc gắn ở trang cá nhân) */}
                        <div className="flex justify-center my-3 min-h-[112px] items-center">
                          {reveal.itemType === 'Nametag' ? (
                            <Nametag banner previewRarity={rar}>
                              <span className="font-semibold">{myName}</span>
                            </Nametag>
                          ) : (
                            <div className="relative" style={{ width: 96, height: 96 }}>
                              <img
                                src={myAvatar}
                                alt=""
                                className="w-24 h-24 rounded-full object-cover"
                              />
                              <AvatarFrame rarity={rar} sizePx={96} withParticles />
                            </div>
                          )}
                        </div>

                        <div className="text-base font-bold ui-text">{reveal.itemName}</div>
                        <div className="ui-muted text-sm">
                          {reveal.itemType === 'Nametag' ? 'Khung tên' : 'Khung avatar'} —{' '}
                          {reveal.expiresInDays ? `dùng ${reveal.expiresInDays} ngày` : 'vĩnh viễn'}
                        </div>
                      </>
                    )}

                    {/* Nút hành động: cosmetic → vào Kho đồ; Ca/Miss → đóng */}
                    {isItem ? (
                      <div className="mt-4 flex flex-col gap-2">
                        <Button
                          type="primary"
                          block
                          onClick={() => {
                            setReveal(null);
                            navigate(routePaths.inventory);
                          }}
                        >
                          Xem trong Kho đồ →
                        </Button>
                        <Button type="text" block onClick={() => setReveal(null)}>
                          Đóng
                        </Button>
                      </div>
                    ) : (
                      <Button className="mt-4" type="primary" block onClick={() => setReveal(null)}>
                        {reveal.kind === 'Ca' ? 'Tuyệt vời!' : 'Thử lại sau'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
      </Modal>
    </div>
  );
};

export default DaoCaPage;
